package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.LocalDensity
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/** One line on a [TrendLineChart]. [label] is caller-localized (DS components carry no copy). */
@Immutable
data class ChartSeries(
    val label: String,
    val values: List<Float>,
    val color: Color,
)

/**
 * Minimal multi-series trend chart (Stitch "Engagement Trends"): stroked polylines over a soft
 * gradient fill of the first series, plus a legend. Series share the x-axis (one point per slot) and
 * are normalized against the global max, so relative magnitude between series stays truthful.
 * Axis labels/tooltips are deliberately out of scope until a real charting need arrives.
 */
@Composable
fun TrendLineChart(
    series: List<ChartSeries>,
    modifier: Modifier = Modifier,
) {
    val strokeWidthPx = with(LocalDensity.current) { AppTheme.sizes.chartStroke.toPx() }
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        val maxValue = series.maxOfOrNull { s -> s.values.maxOrNull() ?: 0f }?.takeIf { it > 0f } ?: 1f
        val fillColor = series.firstOrNull()?.color ?: AppTheme.colors.primary
        Canvas(
            modifier = Modifier
                .fillMaxWidth()
                .height(AppTheme.sizes.chartHeight)
                .clip(AppTheme.shapes.md),
        ) {
            series.forEachIndexed { index, s ->
                if (s.values.size < 2) return@forEachIndexed
                val stepX = size.width / (s.values.size - 1)
                fun yOf(v: Float): Float = size.height * (1f - (v / maxValue) * 0.9f)
                val line = Path().apply {
                    s.values.forEachIndexed { i, v ->
                        if (i == 0) moveTo(0f, yOf(v)) else lineTo(i * stepX, yOf(v))
                    }
                }
                // Gradient area under the FIRST series only — keeps overlapping series readable.
                if (index == 0) {
                    val area = Path().apply {
                        addPath(line)
                        lineTo(size.width, size.height)
                        lineTo(0f, size.height)
                        close()
                    }
                    drawPath(
                        path = area,
                        brush = Brush.verticalGradient(
                            colors = listOf(fillColor.copy(alpha = 0.25f), fillColor.copy(alpha = 0f)),
                        ),
                    )
                }
                drawPath(
                    path = line,
                    color = s.color,
                    style = Stroke(width = strokeWidthPx, cap = StrokeCap.Round, join = StrokeJoin.Round),
                )
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg)) {
            series.forEach { s ->
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                ) {
                    Box(
                        modifier = Modifier
                            .size(AppTheme.sizes.chartLegendDot)
                            .clip(AppTheme.shapes.pill)
                            .background(s.color),
                    )
                    Text(
                        text = s.label,
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                }
            }
        }
    }
}
